'use client';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'next/navigation';
import { getRandomAuthCode } from '@/utils';
import { useQuery } from '@/api/trpc';
import { apiEnvironment, allApiEnvironments } from '@/data/environment';
import ToroSession from '@/api/ToroSession';
import type { TsrContract, ClientInferResponseBody } from '@/api/tsr';
import { AccountDTO } from '@repo/common/schemas/models';
import { EmbeddedAccountProvider } from '@/components/layouts/AuthLayouts';

type EmbeddedAuthResponse = ClientInferResponseBody<TsrContract['embedded']['authenticate'], 200>;
type Headers = Record<string, string>;

const demoOrgCorpId = '26902';
function getBaseUrl(environmentName?: string | null) {
  const environmentConfig =
    allApiEnvironments.find(({ id }) => id === environmentName) || apiEnvironment;
  return environmentConfig.baseUrl + '/embedded';
}

export class BullhornSession {
  accountId?: string;
  account?: AccountDTO | null = null;
  baseUrl: string;
  corpId: string;
  bullhornUserId: string;
  toroUserId: string | null = null;
  sessionId: string | null = null;
  sessionToken: string | null = null;
  userEmail: string | null = null;
  departmentKey: string[] | null = null;

  defaultHeaders: Headers = {
    'Content-Type': 'application/json',
  };

  constructor(corpId: string, bullhornUserId: string, environmentName?: string | null) {
    corpId ||= demoOrgCorpId;
    this.corpId = corpId;
    this.baseUrl = getBaseUrl(environmentName);
    this.bullhornUserId = bullhornUserId;
  }

  private async _sendRequest(
    method: 'GET' | 'POST',
    url: string,
    data: object | null,
    headers: Headers = {}
  ) {
    headers = {
      ...this.defaultHeaders,
      ...headers,
    };
    url = url.replace(/^\//, '');
    const response = await fetch(`${this.baseUrl}/${url}`, {
      method,
      headers,
      body: data && JSON.stringify(data),
    });

    if (!response.ok) {
      let errorMessage = 'Network response was not ok';
      try {
        const responseData = await response.json();
        console.log(responseData);
        errorMessage = responseData?.error || responseData?.err || errorMessage;
      } catch (err) {}
      // hideSpinner();
      // alert('Error: Unable to locate candidate activities. Check your search and try again.');
      throw new Error(errorMessage);
    }
    const responseData = await response.json();
    console.log(responseData);
    return responseData;
  }

  async authenticate(authCode: string) {
    const { corpId, bullhornUserId } = this;
    const params = {
      corpId,
      bullhornUserId,
      authCode,
    };
    console.log('authenticating');
    const response: EmbeddedAuthResponse = await this._sendRequest('POST', '/authenticate', params);
    console.log(response);

    const {
      sessionToken,
      accountId,
      account,
      // TODO: Adjust frontend & backend logic to use authUserId and/or recruiterId
      userId,
      recruiterId,
      authUserId,
      sessionId,
      userEmail,
      departmentKey,
    } = response;

    if (!sessionToken && userId && sessionId) {
      const err = new Error('No session data in auth response');
      console.error(err);
      throw err;
    }

    this.sessionToken = sessionToken;
    this.accountId = accountId;
    this.account = account ?? null;
    this.defaultHeaders['Authorization'] = `Bearer ${sessionToken}`;
    this.toroUserId = userId;
    this.defaultHeaders['toro-user-id'] = userId;
    this.sessionId = sessionId;
    // this.defaultHeaders['toro-session-id'] = sessionId;
    this.userEmail = userEmail;
    this.departmentKey = departmentKey;
  }
}

export const getSession = async ({
  environmentName,
  corpId,
  userId,
  authCode,
}: BullhornSessionParams) => {
  const session = new BullhornSession(corpId, userId, environmentName);
  await session.authenticate(authCode);
  return session;
};

export const BullhornSessionContext = createContext<{
  session?: BullhornSession;
  isLoading?: boolean;
  error?: unknown;
}>({ isLoading: true });

export interface BullhornSessionParams {
  corpId: string;
  userId: string;
  authCode: string;
  environmentName?: string | null;
}

function useAsyncState<T>(fn: () => Promise<T>) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  const [data, setData] = useState<T>();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await fn();
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [fn]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { isLoading, error, data };
}

function useBullhornIFrameSession() {
  const searchParams = useSearchParams();

  const corpId = searchParams.get('CorporationID') || '';
  const userId = searchParams.get('UserID') || '5';

  // If no authCode was provided, generate a random one and persist it across re-renders with useMemo
  const bullhornAuthCode = searchParams.get('authCode') || '';
  const authCode = useMemo(() => bullhornAuthCode || getRandomAuthCode(), [bullhornAuthCode]);
  const environmentName = searchParams.get('environment');

  const params: BullhornSessionParams = useMemo(
    () => ({ corpId, userId, authCode, environmentName }),
    [corpId, userId, authCode, environmentName]
  );
  const queryFn = useCallback(() => getSession(params), [params]);

  const { data: session, isLoading, error } = useAsyncState(queryFn);

  return { session, isLoading, error };
}

export function BullhornIframeSessionWrapper({ children }: { children: ReactNode }) {
  const { session, isLoading, error } = useBullhornIFrameSession();

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ session, isLoading, error }), [session, isLoading, error]);

  return (
    <BullhornSessionContext.Provider value={contextValue}>
      <ToroSession>
        <EmbeddedAccountProvider account={session?.account}>{children}</EmbeddedAccountProvider>
      </ToroSession>
    </BullhornSessionContext.Provider>
  );
}

export function useBullhornSessionContext() {
  const result = useContext(BullhornSessionContext);
  return result;
}

// export function BullhornDashboardSessionWrapper({children, params}: {children: ReactNode; params: BullhornSessionParams}){
//     const {session, isLoading, error} = useBullhornSession(params);
//     return <BullhornSessionContext.Provider value={{session, isLoading, error}}>
//         {children}
//     </BullhornSessionContext.Provider>;
// }
